import type { Cart, RegistrationWizardState } from '@/types';
import { siteConfig } from '@/config/site.config';

const STORAGE_KEY_PREFIX = 'tourney_cart';
const CART_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

interface StoredCart {
  cart: Cart;
  wizardState: Partial<RegistrationWizardState>;
  userId?: string;
  eventId: string;
  timestamp: number;
  expiresAt: number;
}

function getStorageKey(userId?: string): string {
  const userPart = userId || 'anonymous';
  return `${STORAGE_KEY_PREFIX}_${siteConfig.eventId}_${userPart}`;
}

/**
 * Save cart and wizard state to localStorage
 */
export function saveCart(
  cart: Cart,
  wizardState: Partial<RegistrationWizardState>,
  userId?: string
): void {
  try {
    const now = Date.now();
    const data: StoredCart = {
      cart,
      wizardState,
      userId,
      eventId: siteConfig.eventId,
      timestamp: now,
      expiresAt: now + CART_EXPIRY_MS,
    };

    localStorage.setItem(getStorageKey(userId), JSON.stringify(data));
  } catch (error) {
    console.error('Error saving cart:', error);
  }
}

/**
 * Load cart and wizard state from localStorage
 */
export function loadCart(userId?: string): StoredCart | null {
  try {
    const key = getStorageKey(userId);
    const stored = localStorage.getItem(key);

    if (!stored) {
      // Try anonymous cart if user is now logged in
      if (userId) {
        const anonCart = loadCart();
        if (anonCart) {
          // Migrate anonymous cart to user cart
          saveCart(anonCart.cart, anonCart.wizardState, userId);
          clearCart(); // Clear anonymous cart
          return loadCart(userId);
        }
      }
      return null;
    }

    const data: StoredCart = JSON.parse(stored);

    // Check if cart is expired
    if (Date.now() > data.expiresAt) {
      clearCart(userId);
      return null;
    }

    // Check if cart is for the current event
    if (data.eventId !== siteConfig.eventId) {
      clearCart(userId);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error loading cart:', error);
    return null;
  }
}

/**
 * Clear cart from localStorage
 */
export function clearCart(userId?: string): void {
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch (error) {
    console.error('Error clearing cart:', error);
  }
}

/**
 * Get cart expiry time
 */
export function getCartExpiry(userId?: string): Date | null {
  const stored = loadCart(userId);
  if (!stored) return null;
  return new Date(stored.expiresAt);
}

/**
 * Check if cart is about to expire (within 1 hour)
 */
export function isCartExpiringSoon(userId?: string): boolean {
  const expiry = getCartExpiry(userId);
  if (!expiry) return false;
  const oneHour = 60 * 60 * 1000;
  return expiry.getTime() - Date.now() < oneHour;
}

/**
 * Extend cart expiry by 24 hours
 */
export function extendCartExpiry(userId?: string): void {
  const stored = loadCart(userId);
  if (stored) {
    stored.expiresAt = Date.now() + CART_EXPIRY_MS;
    localStorage.setItem(getStorageKey(userId), JSON.stringify(stored));
  }
}

/**
 * Generate idempotency key for checkout
 */
export function generateIdempotencyKey(cart: Cart, userId?: string): string {
  const cartHash = hashCart(cart);
  const timestamp = Math.floor(Date.now() / 1000 / 60); // Round to minute
  return `${siteConfig.eventId}_${userId || 'anon'}_${cartHash}_${timestamp}`;
}

/**
 * Hash cart contents for idempotency
 */
function hashCart(cart: Cart): string {
  const content = cart.items
    .map(item => `${item.type}:${item.itemId}:${item.quantity}:${item.variant?.name || ''}`)
    .sort()
    .join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Store idempotency key to prevent duplicate submissions
 */
export function storeIdempotencyKey(key: string): void {
  const usedKeys = getUsedIdempotencyKeys();
  usedKeys.push({ key, timestamp: Date.now() });

  // Keep only keys from last hour
  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  const filtered = usedKeys.filter(k => k.timestamp > oneHourAgo);

  localStorage.setItem('tourney_idempotency_keys', JSON.stringify(filtered));
}

/**
 * Check if idempotency key was already used
 */
export function isIdempotencyKeyUsed(key: string): boolean {
  const usedKeys = getUsedIdempotencyKeys();
  return usedKeys.some(k => k.key === key);
}

function getUsedIdempotencyKeys(): Array<{ key: string; timestamp: number }> {
  try {
    const stored = localStorage.getItem('tourney_idempotency_keys');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}
